/* global growlMessages, confirmPanel, Promise, alert */
/*
 * Copyright 2026 Collaterate. All rights reserved.
 *
 * data-profiles-page.js
 *
 * Storefront account-page controller for the Data Profiles CRUD UI.
 * Reads window.dataProfilesPageConfig set by the data_profiles.jsp tag-include
 * (mode="manage"), rendered by the [[ dataProfiles ]] theme tag.
 *
 * REST endpoints (session-cookie, same-origin):
 *   GET    /rest/storefront/users/v2/{userId}/dataProfiles
 *   GET    /rest/storefront/users/v2/{userId}/dataProfiles/{profileId}
 *   POST   /rest/storefront/users/v2/{userId}/dataProfiles
 *   PUT    /rest/storefront/users/v2/{userId}/dataProfiles/{profileId}
 *   DELETE /rest/storefront/users/v2/{userId}/dataProfiles/{profileId}
 *   POST   /rest/storefront/users/v2/{userId}/dataProfiles/{profileId}/default
 *   GET    /rest/storefront/users/v2/{userId}/dataProfileFields
 *
 * Response envelope: { content: <data> }
 */
(function () {
    'use strict';

    var cfg = window.dataProfilesPageConfig;
    if (!cfg || !cfg.userId) {
        // Not logged in or config missing — silently no-op.
        return;
    }

    var restBase = cfg.restBase || '/rest/storefront';
    var userId   = cfg.userId;

    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------
    var state = {
        profiles:         [],
        availableFields:  [],   // [{id, fieldKey, displayName, inputType, valueType}]
        loading:          true,
        editingProfileId: null,   // id of profile being edited, or null
        addingProfile:    false,  // true when the add form is open
        editDraft:        null    // { name, description, values[] } working copy
    };

    // -------------------------------------------------------------------------
    // REST helpers
    // -------------------------------------------------------------------------

    function profilesBase() {
        return restBase + '/users/v2/' + userId + '/dataProfiles';
    }

    function fieldsUrl() {
        return restBase + '/users/v2/' + userId + '/dataProfileFields';
    }

    function apiFetch(url, method, body) {
        var opts = {
            method:      method || 'GET',
            credentials: 'same-origin',
            headers:     { 'Accept': 'application/json' }
        };
        if (body !== undefined) {
            opts.headers['Content-Type'] = 'application/json';
            opts.body = JSON.stringify(body);
        }
        return fetch(url, opts).then(function (resp) {
            if (!resp.ok) {
                return resp.json().catch(function () {
                    throw new Error('Request failed: ' + resp.status + ' ' + url);
                }).then(function (errBody) {
                    var msg = (errBody && errBody.message) ? errBody.message
                            : ('Request failed: ' + resp.status + ' ' + url);
                    throw new Error(msg);
                });
            }
            // DELETE returns 200 with { content: null }; others return data.
            return resp.json().then(function (d) {
                return (d && d.content !== undefined) ? d.content : d;
            });
        });
    }

    function fetchProfiles() {
        return fetchAllPages(profilesBase());
    }

    /**
     * Fetches all pages from a paged endpoint, accumulating content arrays.
     * The endpoint returns { content: [...], page: { number, size, totalPages } }.
     */
    function fetchAllPages(url) {
        var allItems = [];
        function fetchPage(pageNumber) {
            var separator = url.indexOf('?') >= 0 ? '&' : '?';
            // Paged endpoints bind to PageRequestDTO (nested "page" bean) — use dot
            // notation (page.number / page.size); bracket notation 400s.
            var pagedUrl = url + separator + 'page.number=' + pageNumber + '&page.size=50';
            return fetch(pagedUrl, {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json' }
            }).then(function (resp) {
                if (!resp.ok) {
                    throw new Error('Request failed: ' + resp.status);
                }
                return resp.json();
            }).then(function (data) {
                var items = data.content || [];
                allItems = allItems.concat(items);
                if (data.page && pageNumber < data.page.totalPages) {
                    return fetchPage(pageNumber + 1);
                }
                return allItems;
            });
        }
        return fetchPage(1);
    }

    function fetchAvailableFields() {
        return apiFetch(fieldsUrl());
    }

    function createProfile(payload) {
        return apiFetch(profilesBase(), 'POST', payload);
    }

    function updateProfile(profileId, payload) {
        return apiFetch(profilesBase() + '/' + profileId, 'PUT', payload);
    }

    function deleteProfile(profileId) {
        return apiFetch(profilesBase() + '/' + profileId, 'DELETE');
    }

    function setDefaultProfile(profileId) {
        return apiFetch(profilesBase() + '/' + profileId + '/default', 'POST');
    }

    // -------------------------------------------------------------------------
    // Rendering
    // -------------------------------------------------------------------------

    function escHtml(str) {
        if (str == null) { return ''; }
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function fieldById(id) {
        return state.availableFields.filter(function (f) { return f.id === id; })[0] || null;
    }

    /**
     * Render the input control for a single field value. List-type fields (those
     * whose option DTO carries a non-empty `options` array) render a <select> of
     * the allowed values; everything else renders a free-text input. An existing
     * value that is not among the options is preserved as a "(current)" option so
     * saving never silently drops a previously-stored value.
     */
    function renderValueControl(name, value, field, ariaLabel) {
        var safeName  = escHtml(name);
        var safeLabel = escHtml(ariaLabel || 'value');
        var current   = (value == null) ? '' : String(value);
        var hasOptions = field && Array.isArray(field.options) && field.options.length > 0;

        if (!hasOptions) {
            return '<input type="text" class="field c-data-profiles-value" name="' + safeName
                + '" value="' + escHtml(current) + '" aria-label="' + safeLabel + '">';
        }

        var inList = field.options.indexOf(current) !== -1;
        var optionsHtml = field.options.map(function (opt) {
            var sel = (opt === current) ? ' selected' : '';
            return '<option value="' + escHtml(opt) + '"' + sel + '>' + escHtml(opt) + '</option>';
        }).join('');
        var currentOpt = (current && !inList)
            ? '<option value="' + escHtml(current) + '" selected>' + escHtml(current) + ' (current)</option>'
            : '';
        return '<select class="field c-data-profiles-value" name="' + safeName
            + '" aria-label="' + safeLabel + '">'
            + '<option value="">&mdash; select &mdash;</option>'
            + currentOpt
            + optionsHtml
            + '</select>';
    }

    function renderValueRows(values) {
        if (!values || values.length === 0) {
            return '<p class="c-data-profiles-empty">No field values defined.</p>';
        }
        var rows = values.map(function (v) {
            var field = fieldById(v.siteProfileFieldId);
            var label = escHtml((field && field.displayName) || v.fieldKey || ('field-' + v.siteProfileFieldId));
            var val   = escHtml(v.value || '');
            return '<tr>'
                + '<th scope="row">' + label + '</th>'
                + '<td>' + val + '</td>'
                + '</tr>';
        }).join('');

        return '<table class="formFields c-data-profiles-values"><tbody>' + rows + '</tbody></table>';
    }

    /**
     * Render the field-values section inside the add/edit form.
     * Every available field gets its own row (label + value control),
     * pre-filled with any value already stored on the profile. Leaving a
     * field blank simply means it is not part of the profile; on save only
     * non-blank values are sent.
     */
    function renderFormValueSection(values) {
        if (state.availableFields.length === 0) {
            return '<div class="c-data-profiles-section">'
                + '<h4 class="c-data-profiles-section-title">Field Values</h4>'
                + '<p class="c-data-profiles-empty">No fields are available.</p>'
                + '</div>';
        }

        var valueByFieldId = {};
        (values || []).forEach(function (v) {
            valueByFieldId[v.siteProfileFieldId] = v;
        });

        var rows = state.availableFields.map(function (field, idx) {
            var existing = valueByFieldId[field.id] || null;
            var current  = existing ? existing.value : '';
            var label    = escHtml(field.displayName || field.fieldKey || ('field-' + field.id));
            return '<tr>'
                + '<th scope="row">'
                +   label
                +   '<input type="hidden" name="siteProfileFieldId_' + idx + '" value="' + escHtml(field.id) + '">'
                +   '<input type="hidden" name="fieldKey_' + idx + '" value="' + escHtml(field.fieldKey || '') + '">'
                +   '<input type="hidden" name="valueType_' + idx + '" value="' + escHtml(field.valueType || '') + '">'
                + '</th>'
                + '<td>'
                +   renderValueControl('value_' + idx, current, field, label + ' value')
                + '</td>'
                + '</tr>';
        }).join('');

        return '<div class="c-data-profiles-section">'
            + '<h4 class="c-data-profiles-section-title">Field Values</h4>'
            + '<table class="formFields c-data-profiles-values"><tbody>'
            +   rows
            + '</tbody></table>'
            + '</div>';
    }

    function renderProfileCard(profile) {
        var defaultBadge = profile.defaultProfile
            ? ' <span class="c-data-profiles-badge">Default</span>'
            : '';
        return '<div class="c-data-profiles-card" id="dp-card-' + profile.id + '">'
            + '<h3 class="c-data-profiles-card-title">' + escHtml(profile.name) + defaultBadge + '</h3>'
            + (profile.description
                ? '<p class="c-data-profiles-card-desc">' + escHtml(profile.description) + '</p>'
                : '')
            + renderValueRows(profile.values)
            + '<div class="c-data-profiles-actions">'
            +   '<button type="button" class="btn btn_secondary c-data-profiles-btn c-data-profiles-btn--edit" data-dp-edit="' + profile.id + '">Edit</button>'
            +   (profile.defaultProfile ? ''
                : '<button type="button" class="btn btn_secondary c-data-profiles-btn c-data-profiles-btn--default" data-dp-set-default="' + profile.id + '">Set Default</button>')
            +   ((profile.defaultProfile || profile.systemGenerated) ? ''
                : '<button type="button" class="btn btn_secondary c-data-profiles-btn c-data-profiles-btn--delete" data-dp-delete="' + profile.id + '">Delete</button>')
            + '</div>'
            + '</div>';
    }

    function renderEditForm(profile) {
        /* profile is null when adding, existing profile object when editing */
        var isEdit  = !!profile;
        var name    = isEdit ? escHtml(profile.name) : '';
        var desc    = isEdit ? escHtml(profile.description || '') : '';
        var values  = isEdit ? (profile.values || []) : [];
        var formId  = isEdit ? ('dp-edit-form-' + profile.id) : 'dp-add-form';
        var saveAttr = isEdit ? ('data-dp-save-edit="' + profile.id + '"') : 'data-dp-save-add';
        var cancelAttr = isEdit ? ('data-dp-cancel-edit="' + profile.id + '"') : 'data-dp-cancel-add';

        return '<form class="c-data-profiles-form" id="' + formId + '" novalidate>'
            + '<table class="formFields"><tbody>'
            +   '<tr class="required">'
            +     '<th scope="row"><label for="' + formId + '-name">Profile Name</label></th>'
            +     '<td><input type="text" id="' + formId + '-name" class="field" name="name" value="' + name + '" required></td>'
            +   '</tr>'
            +   '<tr>'
            +     '<th scope="row"><label for="' + formId + '-desc">Description</label></th>'
            +     '<td><input type="text" id="' + formId + '-desc" class="field" name="description" value="' + desc + '"></td>'
            +   '</tr>'
            + '</tbody></table>'
            + renderFormValueSection(values)
            + '<div class="c-data-profiles-actions">'
            +   '<button type="button" class="btn c-data-profiles-btn c-data-profiles-btn--primary" ' + saveAttr + '>'
            +     (isEdit ? 'Save Changes' : 'Add Profile')
            +   '</button>'
            +   '<button type="button" class="btn btn_secondary c-data-profiles-btn c-data-profiles-btn--secondary" ' + cancelAttr + '>Cancel</button>'
            + '</div>'
            + '</form>';
    }

    function renderList() {
        if (state.loading) {
            return '<p class="c-data-profiles-loading">Loading profiles&hellip;</p>';
        }
        if (state.profiles.length === 0 && !state.addingProfile) {
            return '<p class="c-data-profiles-empty">You have no data profiles yet.</p>';
        }

        var cards = state.profiles.map(function (p) {
            if (state.editingProfileId === p.id) {
                return renderEditForm(p);
            }
            return renderProfileCard(p);
        }).join('');

        return cards;
    }

    function render() {
        var container = document.getElementById('dataProfilesPageContainer');
        if (!container) { return; }

        var addSection = '';
        if (state.addingProfile) {
            addSection = '<div class="c-data-profiles-add">'
                + '<h3 class="c-data-profiles-add-title">New Profile</h3>'
                + renderEditForm(null)
                + '</div>';
        } else {
            addSection = '<div class="c-data-profiles-add">'
                + '<button type="button" id="dpAddProfileBtn" class="btn c-data-profiles-btn c-data-profiles-btn--add" data-dp-open-add>'
                + '+ Add Profile'
                + '</button>'
                + '</div>';
        }

        container.innerHTML = '<div class="c-data-profiles">'
            + addSection
            + '<div class="c-data-profiles-list" id="dpProfileList">'
            +   renderList()
            + '</div>'
            + '</div>';
    }

    // -------------------------------------------------------------------------
    // Event binding (delegated, attached ONCE to the persistent container so
    // listeners do not stack across re-renders)
    // -------------------------------------------------------------------------

    function bindEvents(container) {
        container.addEventListener('click', function (e) {
            var target = e.target;

            // Add profile button
            if (target.hasAttribute('data-dp-open-add')) {
                onOpenAdd();
                return;
            }

            // Save new profile
            if (target.hasAttribute('data-dp-save-add')) {
                onSaveAdd();
                return;
            }

            // Cancel add
            if (target.hasAttribute('data-dp-cancel-add')) {
                onCancelAdd();
                return;
            }

            // Edit profile
            var editId = target.getAttribute('data-dp-edit');
            if (editId !== null) {
                onOpenEdit(parseInt(editId, 10));
                return;
            }

            // Save edit
            var saveEditId = target.getAttribute('data-dp-save-edit');
            if (saveEditId !== null) {
                onSaveEdit(parseInt(saveEditId, 10));
                return;
            }

            // Cancel edit
            var cancelEditId = target.getAttribute('data-dp-cancel-edit');
            if (cancelEditId !== null) {
                onCancelEdit();
                return;
            }

            // Set default
            var defaultId = target.getAttribute('data-dp-set-default');
            if (defaultId !== null) {
                onSetDefault(parseInt(defaultId, 10));
                return;
            }

            // Delete
            var deleteId = target.getAttribute('data-dp-delete');
            if (deleteId !== null) {
                onDelete(parseInt(deleteId, 10));
                return;
            }
        });
    }

    // -------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------

    function showError(msg) {
        // Use growlMessages if available (same pattern as addressBookController),
        // otherwise fall back to alert.
        if (typeof growlMessages !== 'undefined') {
            growlMessages.showErrorMessage(msg);
        } else {
            alert(msg); // eslint-disable-line no-alert
        }
    }

    function confirmAction(msg, onConfirm) {
        // Use confirmPanel if available, otherwise native confirm.
        if (typeof confirmPanel !== 'undefined') {
            confirmPanel.confirm(msg, onConfirm, null, { confirmText: 'Confirm' });
        } else if (window.confirm(msg)) { // eslint-disable-line no-alert
            onConfirm();
        }
    }

    function load() {
        state.loading = true;
        render();
        // Fetch profiles and available fields in parallel.
        // Promise.allSettled ensures a failure in one fetch does not discard
        // data that was successfully returned by the other.
        Promise.allSettled([fetchProfiles(), fetchAvailableFields()])
            .then(function (results) {
                var profilesResult = results[0];
                var fieldsResult   = results[1];

                if (profilesResult.status === 'fulfilled') {
                    state.profiles = Array.isArray(profilesResult.value) ? profilesResult.value : [];
                } else {
                    state.profiles = [];
                    showError('Error loading data profiles: ' + profilesResult.reason.message);
                }

                if (fieldsResult.status === 'fulfilled') {
                    state.availableFields = Array.isArray(fieldsResult.value) ? fieldsResult.value : [];
                } else {
                    state.availableFields = [];
                    showError('Error loading available fields: ' + fieldsResult.reason.message);
                }

                state.loading = false;
                render();
            });
    }

    function onOpenAdd() {
        state.addingProfile    = true;
        state.editingProfileId = null;
        render();
    }

    function onCancelAdd() {
        state.addingProfile = false;
        render();
    }

    /**
     * Read all siteProfileFieldId_N / value_N / valueType_N triples from the form.
     * Every available field has a row; only rows with a non-blank value are sent.
     * Returns { name, description, values } ready for the REST payload.
     */
    function buildPayloadFromForm(form) {
        var name        = form.elements.name ? form.elements.name.value.trim() : '';
        var description = form.elements.description ? form.elements.description.value.trim() : '';

        var values = [];
        var idx = 0;
        while (form.elements['siteProfileFieldId_' + idx]) {
            var fieldIdStr = form.elements['siteProfileFieldId_' + idx].value;
            var value      = form.elements['value_' + idx] ? form.elements['value_' + idx].value : '';
            /* Only include fields the user actually gave a value for. */
            if (fieldIdStr && value && value.trim() !== '') {
                values.push({
                    siteProfileFieldId: parseInt(fieldIdStr, 10),
                    fieldKey:           form.elements['fieldKey_' + idx].value || null,
                    value:              value,
                    valueType:          form.elements['valueType_' + idx].value || null
                });
            }
            idx++;
        }

        return { name: name, description: description, values: values };
    }

    function onSaveAdd() {
        var form = document.getElementById('dp-add-form');
        if (!form) { return; }
        var payload = buildPayloadFromForm(form);
        if (!payload.name) {
            showError('Profile name is required.');
            return;
        }
        createProfile(payload).then(function () {
            state.addingProfile = false;
            load();
        }).catch(function (err) {
            showError('Error creating profile: ' + err.message);
        });
    }

    function onOpenEdit(profileId) {
        state.editingProfileId = profileId;
        state.addingProfile    = false;
        render();
    }

    function onCancelEdit() {
        state.editingProfileId = null;
        render();
    }

    function onSaveEdit(profileId) {
        var form = document.getElementById('dp-edit-form-' + profileId);
        if (!form) { return; }
        var payload = buildPayloadFromForm(form);
        if (!payload.name) {
            showError('Profile name is required.');
            return;
        }
        updateProfile(profileId, payload).then(function () {
            state.editingProfileId = null;
            load();
        }).catch(function (err) {
            showError('Error updating profile: ' + err.message);
        });
    }

    function onSetDefault(profileId) {
        setDefaultProfile(profileId).then(function () {
            load();
        }).catch(function (err) {
            showError('Error setting default profile: ' + err.message);
        });
    }

    function onDelete(profileId) {
        var profile = state.profiles.filter(function (p) { return p.id === profileId; })[0];
        var name    = profile ? escHtml(profile.name) : 'this profile';
        confirmAction('Are you sure you want to delete "' + name + '"?', function () {
            deleteProfile(profileId).then(function () {
                state.profiles = state.profiles.filter(function (p) { return p.id !== profileId; });
                render();
            }).catch(function (err) {
                showError('Error deleting profile: ' + err.message);
            });
        });
    }

    // -------------------------------------------------------------------------
    // Init
    // -------------------------------------------------------------------------

    function init() {
        var container = document.getElementById('dataProfilesPageContainer');
        if (!container) { return; }
        bindEvents(container);
        load();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

}());
